import { SimpleTag } from '@/models/Tag';
import { XMarkIcon } from '@heroicons/react/24/outline';
import React from 'react';
import ConditionalRenderer from '../../ConditionalRenderer';
import Skeleton from '../../Skeleton';
import Tag from '../../dataDisplay/Tag';
import Text from '../../dataDisplay/Text';
import { BaseInputProps } from '../BaseInput';
import { twMerge } from 'tailwind-merge';
import { ClassNameComponent } from '@/utils/ClassNameComponent';
import Popup from '../../Popup';

export interface TagInputProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'className'>,
    BaseInputProps {
  tags?: SimpleTag[];
  onDelete?: (tag: SimpleTag) => void;
  addTagComponent: React.ReactNode;
  className?: ClassNameComponent & {
    tag?: string;
  };
}

type HandleDeleteArgs = {
  tag: SimpleTag;
  event: React.MouseEvent;
};

export default function TagInput(props: TagInputProps) {
  const {
    tags,
    disabled,
    id,
    className,
    errorLabelText,
    onDelete,
    label,
    addTagComponent,
    isLoading,
  } = props;

  const handleDelete = ({ tag, event }: HandleDeleteArgs) => {
    event.stopPropagation();
    onDelete?.(tag);
  };

  return (
    <div
      className={twMerge(
        'form-control relative flex flex-col',
        className?.base,
      )}
    >
      <Popup
        disabled={disabled}
        placement="bottom-start"
        reference={
          <div className="flex flex-col grow">
            <label
              htmlFor={id}
              className={twMerge(
                'label p-0 text-14 cursor-pointer',
                className?.label,
              )}
            >
              {label}
            </label>
            <ConditionalRenderer
              condition={!isLoading}
              fallback={
                <Skeleton
                  testId="tagInputSkeleton"
                  className={`w-full h-8 rounded-md bg-primary-content ${
                    className ?? 'lg:max-w-[100%]'
                  }`}
                />
              }
            >
              <div
                className={twMerge(
                  'flex items-center gap-2 min-h-8 rounded-lg px-2.5 py-1 border border-primary flex-wrap',
                  disabled && 'opacity-50 border-neutral-content',
                )}
                data-testid="tagPreview"
              >
                {tags?.map(tag => {
                  return (
                    <Tag
                      key={tag.id}
                      testId="selectedTag"
                      color="custom"
                      text={tag.name}
                      style={{
                        backgroundColor: tag.color || 'var(--primary)',
                        color: 'var(--base-100)',
                      }}
                      className={{
                        base: twMerge(
                          'text-14 shadow-default h-5 px-2.5 gap-1 flex-row-reverse items-center',
                          className?.tag,
                        ),
                      }}
                      icon={
                        <XMarkIcon
                          className="w-3.5"
                          onClick={event => handleDelete({ tag, event })}
                        />
                      }
                    />
                  );
                })}
              </div>
            </ConditionalRenderer>
          </div>
        }
      >
        <div className="border-[1px] relative w-[432px] border-base-content/20 rounded-md">
          {addTagComponent}
        </div>
      </Popup>

      <ConditionalRenderer condition={!!errorLabelText}>
        <Text text={errorLabelText} className="pt-2 text-primary" />
      </ConditionalRenderer>
    </div>
  );
}
